import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { X, ArrowRight, Calendar, Clock } from "lucide-react";
import { POSTS } from "@/data/blog";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import StickyCallBar from "@/components/site/StickyCallBar";
import QuoteForm from "@/components/site/QuoteForm";

const SITE_URL = "https://honeybeeroofers.com";

function fmt(d) {
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function Blog() {
  const [modal, setModal] = useState(false);
  useEffect(() => {
    document.title = "Roofing Blog | Tips for Snohomish, King & Skagit County | Honey Bee";
    window.scrollTo(0, 0);
    let c = document.head.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement("link"); c.setAttribute("rel", "canonical"); document.head.appendChild(c); }
    c.setAttribute("href", `${SITE_URL}/blog`);
  }, []);

  return (
    <div className="bg-obsidian min-h-screen">
      <Header onQuote={() => setModal(true)} />
      <section className="pt-36 pb-16 honeycomb-bg">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">The HoneyBee Blog</p>
          <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            Roofing Tips From Your Local Crew
          </h1>
          <p className="text-lg text-zinc-400 mt-5 max-w-2xl">
            Straight talk on roof repair, replacement, and storm damage across Snohomish, King &amp; Skagit County — from the folks who actually do the work.
          </p>
        </div>
      </section>

      <section className="pb-24 bg-obsidian">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-8">
          {POSTS.map((p, i) => (
            <motion.div key={p.slug}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.5 }}>
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                className="group block rounded-md border border-white/10 bg-carbon overflow-hidden hover:border-honey/40 transition-colors h-full">
                <div className="h-52 overflow-hidden">
                  <img src={p.heroImage} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-zinc-500 mb-3">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmt(p.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {p.readTime}</span>
                  </div>
                  <h2 className="text-xl font-display font-bold text-white leading-snug">{p.title}</h2>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-honey group-hover:gap-2.5 transition-all">
                    Read the post <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <Footer />
      <StickyCallBar onQuote={() => setModal(true)} />

      {modal && (
        <div data-testid="quote-modal" onClick={() => setModal(false)}
          data-lenis-prevent className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
            className="relative bg-carbon border border-white/10 rounded-md p-6 sm:p-8 w-full max-w-md my-8">
            <button onClick={() => setModal(false)} data-testid="quote-modal-close" className="absolute top-4 right-4 text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-display font-bold text-white mb-1">Free Roof Evaluation</h2>
            <p className="text-sm text-zinc-400 mb-5">Fixed, no-hassle pricing in 30 seconds.</p>
            <QuoteForm onSuccess={() => {}} />
          </motion.div>
        </div>
      )}
    </div>
  );
}
